/**
 * FunnelAttributionView — full-page drill-down on acquisition source quality.
 *
 * In the FisheWear demo all funnel events are pageview-typed, so this view
 * focuses on **source quality**: volume + engagement depth + Meta share +
 * persona LTV capacity per source.
 *
 * Sections:
 *  1. Hero KPI strip (sources, pageviews, engagement, Meta share)
 *  2. Channel mix donut + table
 *  3. Source ranking with engagement quality bars
 *  4. Persona LTV capacity board
 *  5. AI Action Recommendations sidebar
 */
import { useEffect, useState } from 'react';
import {
  Activity,
  ArrowDownRight,
  Crown,
  Filter,
  Globe,
  Network,
  Radar,
  Users,
  Wifi,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchChannelBreakdown,
  type WorkbenchFunnelAttributionResponse,
  type WorkbenchFunnelSource,
  type WorkbenchSourceChannel,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import { EmbeddedChatPanel, type ChatSeedInput } from './EmbeddedChatPanel';
import {
  ActionRecommendations,
  COMPACT,
  CURRENCY,
  DrillDownHeader,
  KpiTile,
  NUMBER,
} from './_drilldownShared';

const CHANNEL_COLORS: Record<WorkbenchSourceChannel, string> = {
  meta: 'bg-teal-500',
  search: 'bg-indigo-500',
  email: 'bg-amber-400',
  direct: 'bg-slate-400',
  other: 'bg-slate-300',
};

const CHANNEL_BADGE: Record<WorkbenchSourceChannel, string> = {
  meta: 'bg-teal-100 text-teal-700 ring-teal-200',
  search: 'bg-indigo-100 text-indigo-700 ring-indigo-200',
  email: 'bg-amber-100 text-amber-700 ring-amber-200',
  direct: 'bg-slate-100 text-slate-600 ring-slate-200',
  other: 'bg-slate-100 text-slate-500 ring-slate-200',
};

function HeroKpis({ data }: { data: WorkbenchFunnelAttributionResponse }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <KpiTile
        label="Acquisition Sources"
        value={String(data.source_count)}
        sub={`${data.channels.length} channels active`}
        icon={<Globe size={14} className="text-slate-500" />}
      />
      <KpiTile
        label="Total Pageviews"
        value={NUMBER.format(data.total_pageviews)}
        sub={`${NUMBER.format(data.total_events)} total events`}
        icon={<Activity size={14} className="text-indigo-500" />}
      />
      <KpiTile
        label="Engagement Depth"
        value={`${data.avg_engagement.toFixed(1)} evt/v`}
        sub="events per acquired visitor"
        icon={<Wifi size={14} className="text-emerald-500" />}
      />
      <KpiTile
        label="Meta Share"
        value={`${data.meta_share_pct.toFixed(0)}%`}
        sub={
          data.meta_share_pct >= 70
            ? 'concentration risk'
            : data.meta_share_pct >= 50
            ? 'healthy primary channel'
            : 'room to grow'
        }
        icon={<Network size={14} className="text-teal-500" />}
        tone={data.meta_share_pct >= 70 ? 'warning' : 'success'}
      />
      <KpiTile
        label="Top Persona LTV"
        value={
          data.personas[0]
            ? CURRENCY.format(data.personas[0].avg_ltv)
            : '—'
        }
        sub={data.personas[0]?.persona_name ?? 'no LTV data'}
        icon={<Crown size={14} className="text-teal-500" />}
        tone="success"
      />
    </div>
  );
}

function ChannelMix({
  channels,
  onAsk,
}: {
  channels: WorkbenchChannelBreakdown[];
  onAsk: (s: ChatSeedInput) => void;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
          <Network size={14} className="text-teal-500" />
          Channel Mix
        </h2>
        <span className="text-[10px] text-slate-400 font-mono">share of pageview acquisition</span>
      </div>
      {/* Stacked bar */}
      <div className="flex h-8 rounded-lg overflow-hidden ring-1 ring-slate-100">
        {channels.map((c) => (
          <div
            key={c.channel}
            className={clsx('flex items-center justify-center', CHANNEL_COLORS[c.channel])}
            style={{ width: `${c.share_pct}%` }}
            title={`${c.channel}: ${c.share_pct}%`}
          >
            {c.share_pct >= 8 && (
              <span className="text-[10px] text-white font-bold">{c.share_pct.toFixed(0)}%</span>
            )}
          </div>
        ))}
      </div>
      <div className="space-y-1.5">
        {channels.map((c) => (
          <button
            key={c.channel}
            onClick={() =>
              onAsk({
                question: `Tell me what I should know about the '${c.channel}' channel — its persona quality, scaling potential, and risks.`,
                chart_axis: { series: 'funnel', channel: c.channel },
              })
            }
            className="w-full flex items-center gap-3 px-2 py-1.5 hover:bg-slate-50 rounded-lg transition-colors text-left"
          >
            <span className={clsx('w-2.5 h-2.5 rounded-full', CHANNEL_COLORS[c.channel])} />
            <span
              className={clsx(
                'text-[10px] font-bold rounded-full px-1.5 py-0.5 ring-1 capitalize w-16 text-center',
                CHANNEL_BADGE[c.channel],
              )}
            >
              {c.channel}
            </span>
            <span className="flex-1 text-[11px] text-slate-700">
              {NUMBER.format(c.pageviews)} pv · {c.events_per_visitor.toFixed(1)} evt/v
            </span>
            <span className="text-[11px] font-bold text-slate-700 shrink-0">
              {c.share_pct.toFixed(0)}%
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SourceTable({
  sources,
  onAsk,
}: {
  sources: WorkbenchFunnelSource[];
  onAsk: (s: ChatSeedInput) => void;
}) {
  const max = Math.max(...sources.map((s) => s.pageview_visitors), 1);
  const sliced = sources.slice(0, 15);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
          <Radar size={14} className="text-indigo-500" />
          Source Ranking
        </h2>
        <span className="text-[10px] text-slate-400 font-mono">top 15 by visitor volume</span>
      </div>
      <div className="space-y-1.5">
        {sliced.map((s) => (
          <div
            key={s.traffic_source}
            className="flex items-center gap-3 px-3 py-2 bg-white border border-slate-100 rounded-lg hover:border-slate-200 transition-colors"
          >
            <span
              className={clsx(
                'text-[9px] font-bold rounded-full px-1.5 py-0.5 ring-1 capitalize w-14 text-center shrink-0',
                CHANNEL_BADGE[s.channel],
              )}
            >
              {s.channel}
            </span>
            <span
              className="text-[11px] font-semibold text-slate-700 flex-1 min-w-0 truncate"
              title={s.traffic_source}
            >
              {s.traffic_source}
            </span>
            <div className="hidden md:block w-32 h-2 bg-slate-100 rounded-full overflow-hidden shrink-0">
              <div
                className={clsx('h-full rounded-full', CHANNEL_COLORS[s.channel])}
                style={{ width: `${(s.pageview_visitors / max) * 100}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-3 shrink-0 text-right">
              <div>
                <div className="text-[9px] text-slate-400">PV</div>
                <div className="text-[11px] font-bold text-slate-700">
                  {NUMBER.format(s.pageview_visitors)}
                </div>
              </div>
              <div>
                <div className="text-[9px] text-slate-400">Eng</div>
                <div
                  className={clsx(
                    'text-[11px] font-bold',
                    s.engagement_quality >= 60 ? 'text-teal-600' : s.engagement_quality >= 30 ? 'text-emerald-600' : 'text-slate-500',
                  )}
                >
                  {s.engagement_quality.toFixed(0)}
                </div>
              </div>
              <div>
                <div className="text-[9px] text-slate-400">Capacity</div>
                <div className="text-[11px] font-bold text-teal-700">
                  {COMPACT.format(s.value_capacity)}
                </div>
              </div>
            </div>
            <button
              onClick={() =>
                onAsk({
                  question: `Tell me about traffic source '${s.traffic_source}' (${s.channel}). Why does it bring ${NUMBER.format(s.pageview_visitors)} visitors with ${s.engagement_quality.toFixed(0)}/100 engagement quality?`,
                  chart_axis: { series: 'funnel', source: s.traffic_source },
                })
              }
              className="text-[10px] text-teal-600 hover:text-teal-700 font-semibold shrink-0 underline-offset-2 hover:underline"
            >
              Ask
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PersonaLtvCapacity({
  data,
  onAsk,
}: {
  data: WorkbenchFunnelAttributionResponse;
  onAsk: (s: ChatSeedInput) => void;
}) {
  if (data.personas.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
          <Users size={14} className="text-indigo-500" />
          Persona LTV Capacity
        </h2>
        <span className="text-[10px] text-slate-400 font-mono">
          where source quality compounds
        </span>
      </div>
      <p className="text-[10px] text-slate-500">
        Each visitor is worth more or less depending on which persona they fit. The
        right column shows what your platform-average LTV looks like by persona —
        higher-LTV personas are the cohorts you want acquisition channels to find.
      </p>
      <div className="space-y-1">
        {data.personas.map((p) => (
          <button
            key={p.persona_name}
            onClick={() =>
              onAsk({
                question: `What attribution sources tend to bring '${p.persona_name}' customers, and how do we acquire more of them?`,
                chart_axis: { series: 'funnel', persona: p.persona_name },
              })
            }
            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 rounded-lg transition-colors text-left"
          >
            <span className="text-[12px] font-semibold text-slate-700 flex-1 min-w-0 truncate">
              {p.persona_name}
            </span>
            <span className="text-[10px] text-slate-400 shrink-0">
              {NUMBER.format(p.purchasers)} purchasers
            </span>
            <span className="text-[12px] font-bold text-teal-700 shrink-0 w-20 text-right">
              {CURRENCY.format(p.avg_ltv)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function FunnelAttributionView() {
  const { clientUid } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchFunnelAttributionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [chatSeed, setChatSeed] = useState<ChatSeedInput | null>(null);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getFunnelAttribution({ clientUid })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientUid]);

  return (
    <div className="space-y-5">
      <DrillDownHeader
        breadcrumb="Performance › Funnel & Source"
        title="Funnel & Source Attribution"
        icon={<Filter size={18} className="text-teal-500" />}
        caption="Where persona-attributed traffic actually comes from — and which sources convert depth."
      />

      {loading && !data && (
        <div className="text-sm text-slate-400 animate-pulse py-12 text-center">
          Loading source attribution...
        </div>
      )}

      {data && (
        <>
          <HeroKpis data={data} />

          {!data.funnel_steps_have_data && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-[11px] text-amber-800">
              <ArrowDownRight size={14} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-bold">Pageview-only event coverage</div>
                Below-fold funnel steps (Product View → ATC → Checkout → Purchase) are
                pageview-typed in the current event stream. We're surfacing
                source-quality signals (volume, engagement depth, persona LTV
                capacity) that don't depend on full funnel telemetry. Backfilling
                event types will light up step conversion analysis automatically.
              </div>
            </div>
          )}

          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-12 lg:col-span-8 space-y-4">
              <ChannelMix channels={data.channels} onAsk={setChatSeed} />
              <SourceTable sources={data.sources} onAsk={setChatSeed} />
              <PersonaLtvCapacity data={data} onAsk={setChatSeed} />
            </div>
            <aside className="col-span-12 lg:col-span-4 space-y-4">
              <ActionRecommendations
                actions={data.actions}
                onActivate={setChatSeed}
                activeSeed={chatSeed}
                emptyText="No source mix recommendations — channel diversity looks healthy."
              />
              {chatSeed && (
                <EmbeddedChatPanel seed={chatSeed} onClose={() => setChatSeed(null)} />
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
